import React from 'react';
import { Truck, MapPin, Clock, CalendarDays, Info } from 'lucide-react';

export default function Shipping() {
  return (
    <div className="container mx-auto px-4 pt-24 pb-12 text-gray-800">
      <h1 className="text-4xl font-bold mb-8 text-center text-brand-secondary">Shipping & Delivery Information</h1>
      
      <p className="text-lg text-center mb-10 max-w-2xl mx-auto">
        At Market Bloom, we partner with local artisan florists to bring beautiful, fresh flowers right to your doorstep. Here's what you need to know about our delivery process.
      </p>

      <div className="space-y-10 max-w-3xl mx-auto">
        <Section title="Local Florist Delivery" icon={<Truck className='text-brand-primary' />}>
          <p>All deliveries are handled with care by our talented local partner florists. This ensures your flowers are fresh, arranged beautifully, and delivered with a personal touch from a member of your community.</p>
        </Section>

        <Section title="Delivery Areas & Fees" icon={<MapPin className='text-brand-primary' />}>
          <p>We deliver to a wide range of suburbs across Australia, thanks to our expanding network of florists. To see if we deliver to your desired location and view specific delivery fees, please enter the recipient's address during the checkout process. Fees are set by individual florists and typically vary based on distance.</p>
        </Section>

        <Section title="Delivery Times" icon={<Clock className='text-brand-primary' />}>
          <p>Many of our florists offer same-day or next-day delivery. Available delivery dates and potential time slots will be shown at checkout once you've entered the delivery address. For specific timing requests, please check the options provided by the florist or consider contacting them directly after placing your order (their details are provided in your order confirmation).</p>
        </Section>

        <Section title="If the Recipient Isn't Home" icon={<Info className='text-brand-primary' />}>
          <p>If the recipient is not available at the time of delivery, the florist will generally leave the flowers in a safe, shaded place if they deem it appropriate. If not, they may attempt to contact the recipient or sender to arrange an alternative. Please provide accurate contact details and any special delivery instructions (e.g., 'Leave at front porch') at checkout to help ensure a smooth delivery.</p>
        </Section>

        <Section title="Special Delivery Instructions" icon={<Info className='text-brand-primary' />}>
          <p>If you have special instructions for delivery, such as for hospitals, businesses, or gated communities, please include these in the 'delivery instructions' field at checkout. This helps our florists ensure your order arrives safely and correctly.</p>
        </Section>

        <Section title="Peak Periods & Holidays" icon={<CalendarDays className='text-brand-primary' />}>
          <p>During busy floral holidays like Valentine's Day and Mother's Day, we recommend placing your order well in advance to ensure availability and timely delivery. Delivery options and cut-off times may vary during these periods.</p>
        </Section>
      </div>

      <div className="mt-12 text-center">
        <p className="text-md text-gray-600 max-w-xl mx-auto">
          For any further questions regarding shipping or delivery, please don't hesitate to <a href="/contact" className="text-brand-primary underline hover:text-brand-primary/80">contact us</a>.
        </p>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, icon, children }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center mb-3">
        <div className="mr-3 flex-shrink-0 w-6 h-6">{icon}</div>
        <h2 className="text-2xl font-semibold text-brand-secondary">{title}</h2>
      </div>
      <div className="text-gray-700 leading-relaxed">
        {children}
      </div>
    </div>
  );
}; 